<template>
    <div>
        <TinyMCE
            ref="editor"
            :disabled="disabledValue"
            v-bind="$attrs"
            :init="editorInit"
            model-events="change input undo redo"
            v-on="$listeners"
        ></TinyMCE>
        <MMSelectImageDialog v-model="showSelectImageDlg" style="z-index: 10000" @select="onSelectImage" />
        <TinyMceSelectDocumentDialog
            v-if="context"
            v-model="showSelectDocumentDlg"
            style="z-index: 10000"
            :record="record"
            :context="context"
            @select="onSelectDocument"
            @close="showSelectDocumentDlg = false"
        />
    </div>
</template>

<script>
import TinyMCE from '@tinymce/tinymce-vue';
import { getI18nInstance } from '@/i18n';
import MMSelectImageDialog from './media_manager/SelectImageDialog.vue';
import GlobalsMixin from '@/mixins/globals';
import TinyMceSelectDocumentDialog from '@/components/TinyMceSelectDocumentDialog.vue';
import { Service } from '@/models/Service';
import { Magazin } from '@/models/Magazin';

/**
 * TinyMCE wrapper component: wraps the tinymce-vue component and sets some "sane defaults", as well as
 * image selection dialogs.
 *
 * All attributes and listeners are passed to the internal tinymce instance.
 *
 * See https://www.tiny.cloud/docs/integrations/vue/ for additional documentation.
 */
export default {
    name: 'KpTinyMce',
    components: { TinyMCE, MMSelectImageDialog, TinyMceSelectDocumentDialog },
    mixins: [GlobalsMixin],

    // This component need to be part of a form component: We want to check
    // for form props like validity and disabled state:
    inject: {
        form: {
            from: 'form',
            default: null,
        },
    },

    props: {
        // The (additional) init config to be used with the TinyMCE setup - see https://www.tiny.cloud/docs/integrations/vue/#init
        // This is merged with a set of default config options.
        init: {
            type: Object,
            default: () => ({}),
        },
        record: {
            type: [Magazin, Service],
            required: false,
            default: null,
        },
        context: {
            type: String,
            required: false,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            showSelectImageDlg: false,
            showSelectDocumentDlg: false,
        };
    },

    computed: {
        disabledValue() {
            return this.disabled || this.form?.disabled;
        },

        editorInit() {
            return Object.assign(
                {
                    skin: 'oxide',
                    language: getI18nInstance().locale || null,
                    menubar: false,
                    plugins: [
                        'anchor',
                        'autoresize',
                        'autolink',
                        'charmap',
                        'code',
                        'fullscreen',
                        'image',
                        'insertdatetime',
                        'link',
                        'lists',
                        'media',
                        'nonbreaking',
                        'preview',
                        'searchreplace',
                        'table',
                        'template',
                        'visualblocks',
                        'visualchars',
                        'wordcount',
                    ].join(' '),
                    toolbar1:
                        'blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist | cut copy paste',
                    toolbar2: 'template | link unlink image media | code fullscreen preview | undo redo',
                    body_class: this.globalParam('tinymce.body_class') || null,
                    browser_spellcheck: true,
                    relative_urls: false,
                    contextmenu: 'link image inserttable | cell row column deletetable',
                    visual: true,
                    min_height: 250,
                    max_height: 700,
                    link_default_target: '_blank',
                    resize: true,
                    paste_as_text: true,
                    paste_block_drop: true,
                    remove_script_host: true,
                    extended_valid_elements: 'script[*],iframe[*]',
                    entity_encoding: 'raw',
                    image_caption: true,
                    content_css: this.getContentCSSUrl(),
                    block_formats: 'Lead=h2;Zwischentitel=h3;Untertitel=h4;Interview-Frage=h5;Absatz=p;',
                    style_formats: this.globalParam('html_editor_style_formats'),
                    templates: this.globalParam('html_editor_templates'),
                    file_picker_callback: (callback, value, meta) => {
                        if (meta.filetype === 'image') {
                            // We just store the TinyMCE callback, as we open another async dialog here
                            // (see onSelectImage below)
                            this._imageSelectionCallback = callback;
                            this.showSelectImageDlg = true;
                            return;
                        }
                    },
                    setup: (editor) => {
                        editor.ui.registry.addButton('kpDocument', {
                            icon: 'new-document',
                            tooltip: this.$t('link_document'),
                            onAction: () => (this.showSelectDocumentDlg = true),
                        });
                    },
                },
                this.init,
            );
        },
    },

    methods: {
        getTinyMCEEditor() {
            return this.$refs.editor.editor;
        },

        /**
         * This method is called when the media manager returns with a user-selected image.
         * Problem is, we came from the image selection dialog in TinyMCE: We stored the
         * TinyMCE's callback before, so that we now can turn back to tinyMCE instead
         */
        onSelectImage(image) {
            if (image && this._imageSelectionCallback) {
                this._imageSelectionCallback(image.url, { alt: image.title, caption: true });
                this._imageSelectionCallback = null;
            }
            this.showSelectImageDlg = false;
        },

        onSelectDocument(doc) {
            if (doc) {
                this.getTinyMCEEditor().insertContent(`!${doc.name}!`);
            }
            this.showSelectDocumentDlg = false;
        },

        getContentCSSUrl() {
            return this.globalParam('tinymce.content_css_url');
        },
    },
};
</script>
